import { syncPlaybookTool } from './model/playbook-tools';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { Modal } from 'flowbite-react';
import { HiOutlineDocumentText, HiOutlineTrash, HiOutlineArrowUpRight, HiOutlinePlus } from 'react-icons/hi2';

import PageContainer from '../../components/page-container';
import Button from '../../components/button/button';
import TextBox from '../../components/forms/text-box';
import TextArea from '../../components/forms/text-area';
import FormLoader from '../../components/form-loader/form-loader';
import Message from '../../components/toast-message';
import { useAppInfoFromUralParams } from '../../helpers/hooks/common-hook';

import { deleteAgentActionSettings, getAgentActions, saveAgentActionSettings } from '../../services/prompt-ai';
import GenerateDialog from './components/generate-dialog';
// import { createPlaybookGenerationSession } from './model/new-playbook-generation'; // "Create with AI" is hidden for now
import { AUTHORING_MODE_PLAYBOOK, isPlaybookRecord } from './model/playbook-model';

/**
 * Playbook list and creation.
 *
 * Playbooks and canvas workflows are the same kind of record, distinguished by
 * `stm.metadata.authoringMode`, so this lists the app's workflows and marks
 * which ones are playbooks. A brand-new record has no mode yet and is claimed by
 * whichever editor saves it first.
 */

function CreateDialog({ appId, onCancel, onCreated }) {
    // `authoringMode` is stored on the action record, which the list endpoint
    // returns verbatim - that is what lets each editor filter its own records
    // without reading every journey.
    const [form, setForm] = useState({
        name: '',
        description: '',
        // `actionType: 'workflow'` is what makes the backend create the journey
        // record behind this action (action/save -> handleWorkflowAction).
        // Without it only the action row exists, and anything that writes to the
        // journey - saving the definition, adding a variable - fails with
        // "journey not found".
        actionType: 'workflow',
        resourceId: 'workflow',
        parameters: [],
        scheduleOnly: false,
        authoringMode: AUTHORING_MODE_PLAYBOOK,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // flowbite's Modal re-appends its portal container on every render unless
    // `root` is a stable node: it compares `container.parentNode !== root` before
    // defaulting root to document.body, so with root undefined the comparison is
    // always true. Re-appending detaches the inputs and drops focus, which made
    // typing impossible after one character. Anchoring to our own node - the same
    // thing journey-create does - keeps the container put.
    const rootRef = useRef(null);
    // The ref is null on the first render, so one re-render is needed before the
    // modal can be anchored. Forcing it here rather than relying on some other
    // state change means the container settles before the user can type.
    const [rootReady, setRootReady] = useState(false);
    useEffect(() => { if (rootRef.current) setRootReady(true); }, []);

    const submit = async () => {
        if (!form.name?.trim()) { setError('Give the playbook a name.'); return; }
        setSaving(true);
        setError(null);
        try {
            const { status, response } = await saveAgentActionSettings(appId, form);
            if (status === 200 && response?.id) {
                const saved = { ...form, id: response.id };
                setForm(saved); // Retry against this playbook instead of creating another.
                await syncPlaybookTool(appId, saved);
                onCreated(response.id);
            }
            else setError('The playbook could not be created.');
        } catch (e) {
            setError(e.message || 'The playbook could not be created.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div ref={rootRef}>
        <Modal show size="md" popup onClose={onCancel} root={rootReady ? rootRef.current : undefined}>
            <Modal.Header />
            <Modal.Body>
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">New playbook</h3>
                    {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
                    <TextBox
                        label="Name"
                        name="name"
                        placeholder="Order status"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                    <TextArea
                        label="When should the agent use this?"
                        name="description"
                        rows={3}
                        placeholder="The customer wants to know where their order is."
                        value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <Button text="Cancel" color="default" onClick={onCancel} />
                        <Button text="Create" onClick={submit} loading={saving} />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
        </div>
    );
}

export default function PlaybookListPage() {
    const { appId } = useAppInfoFromUralParams();
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const generationSession = useRef(null);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const deletePlaybook = async () => {
        if (deleteBusy || !deleting) return;
        setDeleteBusy(true);
        setDeleteError('');
        try {
            await syncPlaybookTool(appId, deleting, true);
            const { status } = await deleteAgentActionSettings(appId, deleting.id);
            if (status < 200 || status >= 300 || !status) throw new Error('Could not delete the playbook. Please try again.');
            setItems(current => current.filter(item => item.id !== deleting.id));
            setDeleting(null);
        } catch (e) {
            setDeleteError(e.message || 'Could not delete the playbook. Please try again.');
        } finally {
            setDeleteBusy(false);
        }
    };

    const fetchItems = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        try {
            const settings = await getAgentActions(appId);
            // Only this editor's records. A workflow built on the canvas has no
            // authoringMode (or a different one) and belongs in the workflow
            // editor, so it is not listed here.
            setItems((settings?.data || []).filter(isPlaybookRecord));
        } catch (e) {
            setError('Could not load playbooks.');
        } finally {
            setLoading(false);
        }
    }, [appId]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const open = item => navigate(`/app/${appId}/playbooks/${item.id}`);

    return (
        <PageContainer
            title="Playbooks"
            subtitle="Turn your processes into clear, repeatable instructions for your agent."
            headerAction={<>
                {/* <Button text="Create with AI" color="default" onClick={() => { generationSession.current = createPlaybookGenerationSession(appId); setGenerating(true); }} /> */}
                <Button icon={<HiOutlinePlus size={16} />} text="New playbook" onClick={() => setCreating(true)} />
            </>}
        >
            {error && <Message text={error} type="failure" />}

            {loading ? (
                <FormLoader relative text="Loading playbooks" />
            ) : items.length === 0 ? (
                <div className="mt-10 rounded-2xl border border-dashed border-gray-200 px-6 py-12 text-center">
                    <HiOutlineDocumentText className="mx-auto text-gray-300" size={36} />
                    <h3 className="mt-3 text-base font-semibold text-gray-800">No playbooks yet</h3>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
                        A playbook is a numbered list of steps the agent follows — send a message, ask a
                        question, run a tool, branch on the answer.
                    </p>
                    <div className="mt-5 flex justify-center">
                        <Button text="New playbook" onClick={() => setCreating(true)} />
                    </div>
                </div>
            ) : (
                <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {items.map(item => (
                        <article key={item.id} className="group flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 hover:shadow-md">
                            <button type="button" onClick={() => open(item)} className="flex-1 rounded-t-2xl p-5 text-left focus:outline-none focus:ring-2 focus:ring-primary/20">
                                <div className="mb-5 flex items-center justify-between">
                                    <span className="rounded-xl bg-gray-100 p-2.5 text-primary"><HiOutlineDocumentText size={22} /></span>
                                    <HiOutlineArrowUpRight className="text-gray-400 transition group-hover:text-primary" size={18} />
                                </div>
                                <h3 className="truncate font-semibold text-gray-900">{item.name || item.id}</h3>
                                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-gray-500">{item.description || 'Add instructions to guide your agent.'}</p>
                            </button>
                            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
                                <span className="text-xs font-medium text-gray-500">Playbook</span>
                                <button type="button" aria-label={`Delete ${item.name || item.id}`} onClick={() => { setDeleting(item); setDeleteError(''); }} className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-rose-50 hover:text-rose-600 focus:ring-2 focus:ring-rose-200">
                                    <HiOutlineTrash size={15} /> Delete
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {deleting && (
                <Dialog open onClose={() => { if (!deleteBusy) setDeleting(null); }} className="relative z-50">
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                            <Dialog.Title className="text-lg font-semibold text-gray-900">Delete playbook?</Dialog.Title>
                            <Dialog.Description className="mt-2 text-sm text-gray-500">“{deleting.name || deleting.id}” and its steps will be permanently deleted. This cannot be undone.</Dialog.Description>
                            {deleteError && <p role="alert" className="mt-3 text-sm text-rose-600">{deleteError}</p>}
                            <div className="mt-6 flex justify-end gap-2">
                                <Button color="default" text="Cancel" disabled={deleteBusy} onClick={() => setDeleting(null)} />
                                <Button color="error" text="Delete playbook" disabled={deleteBusy} loading={deleteBusy} onClick={deletePlaybook} />
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            )}

            {generating && <GenerateDialog onClose={() => { setGenerating(false); fetchItems(); }} onGenerate={async request => {
                const result = await generationSession.current(request);
                navigate(`/app/${appId}/playbooks/${result.journeyId}`);
            }} />}

            {creating && (
                <CreateDialog
                    appId={appId}
                    onCancel={() => setCreating(false)}
                    onCreated={id => navigate(`/app/${appId}/playbooks/${id}`)}
                />
            )}
        </PageContainer>
    );
}
